import React, { useCallback } from 'react';
import styled from 'styled-components';
import { COLORS } from '../settings/variables/Colors';

interface TrueImpactCheckboxProps {
  checked: boolean;
  text: string | JSX.Element;
  handleCheck: (check: boolean) => void;
  disabled?: boolean;
  className?: string;
}

const CheckboxContainer = styled.label<{ disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  gap: 8px;
  user-select: none;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  padding: 0;
  pointer-events: none;
`;

const StyledCheckbox = styled.div<{ checked: boolean; disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 2px solid
    ${({ checked }) => (checked ? COLORS.ctaPrimary : COLORS.grayBase)};
  border-radius: 3px;
  background-color: ${({ checked }) =>
    checked ? COLORS.ctaPrimary : 'transparent'};
  transition: all 0.2s ease;
  flex-shrink: 0;

  ${({ disabled }) =>
    disabled &&
    `
      border-color: ${COLORS.grayBase};
      background-color: ${COLORS.grayLight};
    `}
`;

const CheckIcon = styled.svg<{ checked: boolean }>`
  width: 10px;
  height: 10px;
  opacity: ${({ checked }) => (checked ? 1 : 0)};
  transition: opacity 0.2s ease;
  fill: none;
  stroke: white;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const LabelText = styled.span<{ disabled?: boolean }>`
  font-size: 14px;
  color: ${({ disabled }) => (disabled ? COLORS.grayDark : COLORS.blackBase)};
`;

const TrueImpactCheckbox: React.FC<TrueImpactCheckboxProps> = ({
  checked,
  text,
  handleCheck,
  disabled = false,
  className,
}) => {
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        handleCheck(e.target.checked);
      }
    },
    [handleCheck, disabled],
  );

  return (
    <CheckboxContainer disabled={disabled} className={className}>
      <HiddenCheckbox
        checked={checked}
        onChange={onChange}
        disabled={disabled}
      />
      <StyledCheckbox checked={checked} disabled={disabled}>
        <CheckIcon checked={checked} viewBox="0 0 24 24">
          <polyline points="20 6 9 17 4 12" />
        </CheckIcon>
      </StyledCheckbox>
      <LabelText disabled={disabled}>{text}</LabelText>
    </CheckboxContainer>
  );
};

export default TrueImpactCheckbox;
